import { Button, Stack, Typography } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';

import { DocumentType, EmulatorType, LegalAcceptanceSource, PlanFamily } from '@localstack/types';
import {
  AWS_KEY_FEATURES,
  AZURE_KEY_FEATURES,
  EMULATOR_LABELS,
  SNOWFLAKE_KEY_FEATURES,
  TEST_IDS,
} from '@localstack/constants';
import { LegalService, SubscriptionService, useApiEffect, useRoutes } from '@localstack/services';
import { AWSEmulatorPill, AzureEmulatorPill, SnowflakeEmulatorPill, StartPlanButton } from '@localstack/ui';

import { useDirectSubscribe } from '~/hooks/useDirectSubscribe';

import { pricingRoute } from '~/util/pricing';

import { OfferedEmulator } from '~/views/customer/PricingPlans/components/planFamilies';

import { FeatureList, GridComponent, PLAN_INFO_WIDTH, SectionContainer } from '../utils';

const AWSPrompt = (): JSX.Element => {
  const { goto } = useRoutes();
  return (
    <SectionContainer data-testid={TEST_IDS.BILLING_EMULATOR_PROMPT(EmulatorType.AWS)}>
      <GridComponent columns={`${PLAN_INFO_WIDTH} 1fr`}>
        <Stack gap="4px">
          <AWSEmulatorPill />
          <Typography color="text.tertiary">Build and test data applications for AWS locally.</Typography>
          <Button
            variant="outlined"
            size="medium"
            endIcon={<ArrowForward />}
            sx={{ width: 'fit-content', marginTop: '20px' }}
            onClick={() => goto(pricingRoute([EmulatorType.AWS]))}
          >
            All Plans
          </Button>
        </Stack>
        <FeatureList features={AWS_KEY_FEATURES} />
      </GridComponent>
    </SectionContainer>
  );
};

const SnowflakePrompt = (): JSX.Element => {
  const { goto } = useRoutes();
  return (
    <SectionContainer data-testid={TEST_IDS.BILLING_EMULATOR_PROMPT(EmulatorType.SNOWFLAKE)}>
      <GridComponent columns={`${PLAN_INFO_WIDTH} 1fr`}>
        <Stack gap="4px">
          <SnowflakeEmulatorPill />
          <Typography color="text.tertiary">Build and test data pipelines for Snowflake locally.</Typography>
          <Button
            variant="outlined"
            size="medium"
            endIcon={<ArrowForward />}
            sx={{ width: 'fit-content', marginTop: '20px' }}
            onClick={() => goto(pricingRoute([EmulatorType.SNOWFLAKE]))}
          >
            All Plans
          </Button>
        </Stack>
        <FeatureList features={SNOWFLAKE_KEY_FEATURES} />
      </GridComponent>
    </SectionContainer>
  );
};

const AzurePrompt = (): JSX.Element => {
  const { subscribeDirectly, isLoading: isSubscribing } = useDirectSubscribe();
  const { recordLegalAcceptance } = useApiEffect(LegalService, ['recordLegalAcceptance']);

  const { listPlans, isLoading: isLoadingPlans } = useApiEffect(SubscriptionService, ['listPlans']);

  const startPreview = async () => {
    const plans = await listPlans(EmulatorType.AZURE);
    const previewPlan = plans?.find((plan) => plan.family === PlanFamily.PREVIEW_PLANS);
    if (!previewPlan) return;

    const subscription = await subscribeDirectly({ planId: previewPlan.id });
    if (!subscription) return;

    await recordLegalAcceptance({
      document_type: DocumentType.MSA,
      source: LegalAcceptanceSource.PRICING_PLANS,
      context: `Implicitly accepted MSA when joining the ${EMULATOR_LABELS[EmulatorType.AZURE]} public preview from the billing page`,
    });
  };

  return (
    <SectionContainer data-testid={TEST_IDS.BILLING_EMULATOR_PROMPT(EmulatorType.AZURE)}>
      <GridComponent columns={`${PLAN_INFO_WIDTH} 1fr`}>
        <Stack gap="4px">
          <AzureEmulatorPill />
          <Typography color="text.tertiary">Build and test applications for Azure locally.</Typography>
          <StartPlanButton
            emulator={EmulatorType.AZURE}
            family={PlanFamily.PREVIEW_PLANS}
            buttonText="Enable Preview"
            size="medium"
            sx={{ width: 'fit-content', marginTop: '20px' }}
            disabled={isLoadingPlans || isSubscribing}
            onClick={startPreview}
          />
        </Stack>
        <FeatureList features={AZURE_KEY_FEATURES} />
      </GridComponent>
    </SectionContainer>
  );
};

const EMULATOR_PROMPT: Record<OfferedEmulator, () => JSX.Element> = {
  [EmulatorType.AWS]: AWSPrompt,
  [EmulatorType.SNOWFLAKE]: SnowflakePrompt,
  [EmulatorType.AZURE]: AzurePrompt,
};

export const EmulatorPrompt = ({ emulator }: { emulator: OfferedEmulator }): JSX.Element => {
  const Prompt = EMULATOR_PROMPT[emulator];
  return <Prompt />;
};
